import { T, RADIUS, SHADOW } from '@/lib/tokens';

interface InvitedUserWelcomeProps {
  groupName: string;
  hasItemHighlighted: boolean;
  onDismiss: () => void;
}

const steps = [
  { icon: '📦', text: 'Browse items your group is sharing' },
  { icon: '🤝', text: 'Request to borrow something you need' },
  { icon: '➕', text: 'Add your own items for others to borrow' },
];

/**
 * Welcome overlay shown to users who join via an invite link.
 * Appears once per group, dismissal tracked in localStorage.
 */
export function InvitedUserWelcome({ groupName, hasItemHighlighted, onDismiss }: InvitedUserWelcomeProps) {
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 50,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0,0,0,0.45)',
        padding: 20,
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 380,
          background: T.surface,
          border: `1px solid ${T.border}`,
          borderRadius: RADIUS.xl,
          boxShadow: SHADOW.lg,
          padding: 28,
          textAlign: 'center',
        }}
      >
        {/* Greeting */}
        <div style={{ fontSize: 32, marginBottom: 12 }}>🎉</div>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: T.text, margin: '0 0 6px' }}>
          You've joined {groupName}!
        </h2>
        {hasItemHighlighted ? (
          <p style={{ fontSize: 14, color: T.textSec, lineHeight: 1.6, margin: '0 0 20px' }}>
            The item you were invited to borrow is highlighted below. Here's what else you can do:
          </p>
        ) : (
          <p style={{ fontSize: 14, color: T.textSec, lineHeight: 1.6, margin: '0 0 20px' }}>
            Here's what you can do:
          </p>
        )}

        {/* Step list */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24, textAlign: 'left' }}>
          {steps.map((step) => (
            <div
              key={step.text}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '10px 14px',
                background: T.accentSoft,
                border: `1px solid ${T.accentBorder}`,
                borderRadius: RADIUS.md,
              }}
            >
              <span style={{ fontSize: 18, flexShrink: 0 }}>{step.icon}</span>
              <span style={{ fontSize: 13, color: T.text, lineHeight: 1.4 }}>{step.text}</span>
            </div>
          ))}
        </div>

        {/* Dismiss */}
        <button
          onClick={onDismiss}
          style={{
            width: '100%',
            padding: '13px',
            borderRadius: RADIUS.md,
            background: T.accent,
            color: T.white,
            border: 'none',
            fontSize: 15,
            fontWeight: 600,
            cursor: 'pointer',
            fontFamily: 'inherit',
            boxShadow: SHADOW.btn.accent,
          }}
        >
          Got it — let's go!
        </button>
      </div>
    </div>
  );
}
